import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import "regenerator-runtime/runtime.js";

/**
 * Create a new analytics instance configured to Nerdwallet's `bedrock` analytics specification
 *
 * @module
 */
import uuid from 'uuid/v4';
import { getCookies as apiSdkgetCookies, getCoreHTTPHeaders } from '@nerdwallet/api-sdk-core';
import createAnalytics from './createAnalytics';
import getNetworkConnectionTypeSafe from './getNetworkConnectionType';
import transportGraphQL from './transportGraphQL';
import { isInBrowser } from './utils';
import { defaultOnError, defaultOnTransportResults } from './defaultHandlers';
import eventSubclasses from './eventSubclasses';
/**
 * Cookie name constants.
 */

var COOKIES = {
  COOKIE_ID: 'cookie_id',
  MEMBER_IDENTITY_ASSERTION_TOKEN: 'member_identity_assertion_token',
  VISITOR_IDENTITY_ASSERTION_TOKEN: 'visitor_identity_assertion_token',
  GOOGLE_ANALYTICS_CLIENT_ID_COOKIE_NAME: '_ga'
};
/**
 * Header name constants.
 */

var HEADERS = {
  X_COOKIE_ID: 'X-Cookie-ID',
  X_CLIENT_PLATFORM: 'X-Client-Platform',
  X_MEMBER_IDENTITY_ASSERTION_TOKEN: 'X-Member-Identity-Assertion-Token',
  X_REQUEST_ID: 'X-Request-ID',
  X_VISITOR_IDENTITY_ASSERTION_TOKEN: 'X-Visitor-Identity-Assertion-Token',
  USER_AGENT: 'User-Agent',
  // Used in Apollo Graph Manager to identify requesting client
  // https://www.apollographql.com/docs/graph-manager/client-awareness/#using-apollo-server-and-apollo-client
  APOLLO_CLIENT_NAME: 'apollographql-client-name',
  APOLLO_CLIENT_VERSION: 'apollographql-client-version'
};
/**
 * Returns identity assertion token (IAT) base_event payload fields reading
 * the values from headers or cookies.
 */

var getIdentityAssertionTokens = function getIdentityAssertionTokens(_ref) {
  var cookies = _ref.cookies,
      serverRequest = _ref.serverRequest;

  // Read from X-*-Identity-Assertion-Token headers
  if (serverRequest) {
    return {
      member_identity_assertion_token: serverRequest.get(HEADERS.X_MEMBER_IDENTITY_ASSERTION_TOKEN),
      visitor_identity_assertion_token: serverRequest.get(HEADERS.X_VISITOR_IDENTITY_ASSERTION_TOKEN)
    };
  } // Read from *_identity_assertion_token cookies


  return {
    member_identity_assertion_token: cookies[COOKIES.MEMBER_IDENTITY_ASSERTION_TOKEN],
    visitor_identity_assertion_token: cookies[COOKIES.VISITOR_IDENTITY_ASSERTION_TOKEN]
  };
};

export var getBaseEventOnCaptureFields = function getBaseEventOnCaptureFields(payload, _ref2) {
  var analytics = _ref2.analytics,
      appConfig = _ref2.appConfig,
      cookies = _ref2.cookies,
      getNetworkConnectionType = _ref2.getNetworkConnectionType,
      getDeviceId = _ref2.getDeviceId,
      serverRequest = _ref2.serverRequest;
  var _appConfig$_meta = appConfig._meta,
      appConfigMeta = _appConfig$_meta === void 0 ? {} : _appConfig$_meta;
  var deployableName = appConfigMeta.deployable,
      deployableEnvironment = appConfigMeta.environment,
      deployableVersion = appConfigMeta.version;
  var eventSeqno = analytics.getState('event_seqno');
  analytics.setState('event_seqno', eventSeqno + 1);
  var fields = {
    frame: _objectSpread(_objectSpread({
      deployable_name: deployableName,
      deployable_environment: deployableEnvironment,
      deployable_version: deployableVersion,
      event_seqno: eventSeqno,
      network_connectivity: getNetworkConnectionType()
    }, getIdentityAssertionTokens({
      cookies: cookies,
      serverRequest: serverRequest
    })), {}, {
      device_id: getDeviceId({
        cookies: cookies,
        serverRequest: serverRequest
      }),
      // Store capture time for use later to calculate
      // event_occurred_msec_ago
      event_occurred_at: Date.now()
    })
  };

  if (serverRequest) {
    var requestId = serverRequest.get(HEADERS.X_REQUEST_ID);

    if (requestId) {
      fields.frame.x_request_id = requestId;
    } // Send web_user_agent with all server events: https://nerdwallet.atlassian.net/browse/FEI-1704


    fields.web_user_agent = serverRequest.get(HEADERS.USER_AGENT);
  } else if (isInBrowser()) {
    // Send web_user_agent with all browser events: https://nerdwallet.atlassian.net/browse/FEI-1457
    fields.web_user_agent = window.navigator.userAgent;
  }

  return fields;
};
export var getBaseEventOnTransportFields = function getBaseEventOnTransportFields(payload) {
  var now = Date.now();
  return {
    frame: {
      event_occurred_msec_ago: now - payload.frame.event_occurred_at,
      // Unset event_occurred_at because it's not expected to be in
      // the payload. It's used only to calculate
      // event_occurred_msec_ago.
      event_occurred_at: undefined
    }
  };
};
export var getDeviceIdWeb = function getDeviceIdWeb(_ref3) {
  var cookies = _ref3.cookies,
      serverRequest = _ref3.serverRequest;
  return (// read cookie_id value first from headers, then from cookies
    serverRequest && serverRequest.get(HEADERS.X_COOKIE_ID) || cookies[COOKIES.COOKIE_ID]
  );
};
/**
 * Create a new analytics instance for tracking and transporting batches of events
 *
 * @param {Object} options={}
 *                   Options used to create and configure analytics instance behavior
 * @param {Object} options.appConfig
 *                   `@nerdwallet/app-config` or an object of compatible shape.
 * @param {String} [options.platform='server']
 *                   Value of 'X-Client-Platform' header to send to `supergraph`
 * @param {Object} [options.serverRequest]
 *                   Express server request `req`
 * @param {Function} options.transport
 *                   (async) Function to transport a batch of events to a remote endpoint,
 *                   default: POST to supergraph with required headers paired from server req
 * @param {Function} [options.getCookies]
 *                   (async) Method to return cookies as object of { [cookie name]: cookieValue },
 *                   default: getCookies from api-sdk-core, pass serverRequest & clientID
 * @param {Function} [options.getDeviceId]
 *                   (async) Method to return a string to use for `device_id` payload fields,
 *                   default: web-stack method to pull `cookie_id` from supplied serverRequest or cookies
 * @param {Function} [options.getNetworkConnectionType]
 *                   Method to read network connection type
 *                   default: getNetworkConnectionType using a method safe for browser and server
 * @param {Number} [options.batchSize=100]
 *                   Maximum number of analytics events that will be batched into one transport call
 * @param {Number} [options.batchDelay=0]
 *                   Duration to wait (in ms) to batch events before making a transport call
 * @param {Object} [options.batchQueue]
 *                   An persistent storage queue that implements asynchronous interface:
 *                   `enqueue(item), dequeue(limit), toArray()`
 * @param {Function} [options.onError]
 *                   Custom error handler (overrides defaultOnError)
 *                   Called with an error object when a transport or enqueue error occurs.
 * @param {Function} [options.onTransportResults]
 *                   Called with transported events and the results of transporting them.
 * @param {Object}  [options.state={}]
 *                   Analytics state.  By default, this is a local empty `{}` object.
 *                   Override this to share a state `object` between different instances of `analytics` from `createNerdwalletAnalytics`
 *
 * @returns {analytics} an analytics instance configured for `bedrock` specifications
 */

export default function createNerdwalletAnalytics() {
  var _appConfig$NWJS_ANALY3, _appConfig$NWJS_ANALY4;

  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  if (!options.appConfig) {
    throw new Error("'options.appConfig' required to 'createNerdwalletAnalytics', got: ".concat(JSON.stringify(options)));
  }

  var appConfig = options.appConfig;
  var clientID = appConfig.CLIENT_ID,
      _appConfig$_meta2 = appConfig._meta,
      appConfigMeta = _appConfig$_meta2 === void 0 ? {} : _appConfig$_meta2;
  var deployableVersion = appConfigMeta.version;
  var serverRequest = options.serverRequest,
      _options$platform = options.platform,
      platform = _options$platform === void 0 ? 'server' : _options$platform,
      _options$getCookies = options.getCookies,
      getCookies = _options$getCookies === void 0 ? /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee() {
    return regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            return _context.abrupt("return", // getCookies from api-sdk-core, pass serverRequest & clientID
            apiSdkgetCookies({
              serverRequest: serverRequest,
              config: {
                clientID: clientID
              }
            }));

          case 1:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  })) : _options$getCookies,
      _options$getDeviceId = options.getDeviceId,
      getDeviceId = _options$getDeviceId === void 0 ? getDeviceIdWeb : _options$getDeviceId,
      _options$getNetworkCo = options.getNetworkConnectionType,
      getNetworkConnectionType = _options$getNetworkCo === void 0 ? function () {
    return (// getNetworkConnectionType using a method safe for browser and server
      getNetworkConnectionTypeSafe()
    );
  } : _options$getNetworkCo,
      _options$transport = options.transport,
      transport = _options$transport === void 0 ? /*#__PURE__*/function () {
    var _ref5 = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee2(events) {
      var _appConfig$API, _appConfig$API$BASE_U, _appConfig$NWJS_ANALY, _appConfig$NWJS_ANALY2, _objectSpread2;

      var SUPERGRAPH_BASE_URL, maxAttempts, maxDelayMs, headers;
      return regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              SUPERGRAPH_BASE_URL = appConfig === null || appConfig === void 0 ? void 0 : (_appConfig$API = appConfig.API) === null || _appConfig$API === void 0 ? void 0 : (_appConfig$API$BASE_U = _appConfig$API.BASE_URLS) === null || _appConfig$API$BASE_U === void 0 ? void 0 : _appConfig$API$BASE_U.SUPERGRAPH;

              if (SUPERGRAPH_BASE_URL) {
                _context2.next = 3;
                break;
              }

              throw new Error('API.BASE_URLS.SUPERGRAPH app-configs must be defined');

            case 3:
              maxAttempts = appConfig === null || appConfig === void 0 ? void 0 : (_appConfig$NWJS_ANALY = appConfig.NWJS_ANALYTICS) === null || _appConfig$NWJS_ANALY === void 0 ? void 0 : _appConfig$NWJS_ANALY.RETRY_MAX_ATTEMPTS;
              maxDelayMs = appConfig === null || appConfig === void 0 ? void 0 : (_appConfig$NWJS_ANALY2 = appConfig.NWJS_ANALYTICS) === null || _appConfig$NWJS_ANALY2 === void 0 ? void 0 : _appConfig$NWJS_ANALY2.RETRY_MAX_DELAY_MS; // transport to supergraph with required headers paired from server req

              _context2.t0 = _objectSpread;
              _context2.t1 = _objectSpread;
              _context2.t2 = {};
              _context2.next = 10;
              return getCoreHTTPHeaders({
                serverRequest: serverRequest,
                config: {
                  clientID: clientID
                }
              });

            case 10:
              _context2.t3 = _context2.sent;
              _context2.t4 = (0, _context2.t1)(_context2.t2, _context2.t3);
              _context2.t5 = {};
              _context2.t6 = (_objectSpread2 = {}, _defineProperty(_objectSpread2, HEADERS.X_CLIENT_PLATFORM, platform), _defineProperty(_objectSpread2, HEADERS.APOLLO_CLIENT_NAME, clientID), _defineProperty(_objectSpread2, HEADERS.APOLLO_CLIENT_VERSION, deployableVersion), _objectSpread2);
              headers = (0, _context2.t0)(_context2.t4, _context2.t5, _context2.t6);
              return _context2.abrupt("return", transportGraphQL(events, headers, _objectSpread(_objectSpread({
                baseUrl: SUPERGRAPH_BASE_URL
              }, maxAttempts ? {
                maxAttempts: maxAttempts
              } : {}), maxDelayMs ? {
                maxDelayMs: maxDelayMs
              } : {})));

            case 16:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2);
    }));

    return function (_x) {
      return _ref5.apply(this, arguments);
    };
  }() : _options$transport,
      _options$batchSize = options.batchSize,
      batchSize = _options$batchSize === void 0 ? 100 : _options$batchSize,
      _options$batchDelay = options.batchDelay,
      batchDelay = _options$batchDelay === void 0 ? 0 : _options$batchDelay,
      batchQueue = options.batchQueue,
      _options$onError = options.onError,
      onError = _options$onError === void 0 ? defaultOnError(appConfig) : _options$onError,
      _options$onTransportR = options.onTransportResults,
      onTransportResults = _options$onTransportR === void 0 ? defaultOnTransportResults(appConfig) : _options$onTransportR,
      _options$state = options.state,
      state = _options$state === void 0 ? {} : _options$state;
  var analytics = createAnalytics({
    // Enable analytics only if NWJS_ANALYTICS.ENABLED is set in app-configs.
    // Disabled when config is not present (e.g., in tests or in libraries
    // without app-configs).
    enabled: (_appConfig$NWJS_ANALY3 = appConfig === null || appConfig === void 0 ? void 0 : (_appConfig$NWJS_ANALY4 = appConfig.NWJS_ANALYTICS) === null || _appConfig$NWJS_ANALY4 === void 0 ? void 0 : _appConfig$NWJS_ANALY4.ENABLED) !== null && _appConfig$NWJS_ANALY3 !== void 0 ? _appConfig$NWJS_ANALY3 : false,
    // batch configuration
    batchDelay: batchDelay,
    batchSize: batchSize,
    batchQueue: batchQueue,
    transport: transport,
    // optional custom error handler
    onError: onError,
    onTransportResults: onTransportResults,
    // optional shared state object
    state: state,
    // bedrock base_event eventType
    eventBaseClass: 'base_event',
    // bedrock event subclass mappings
    eventSubclasses: eventSubclasses,
    // bedrock event decorations
    eventPayloadDecorators: [// add `bedrock` common event fields when any event is tracked/captured
    {
      // base_event decorations are applied to EVERY event
      // https://github.com/NerdWallet/app-schemata/blob/master/analytic_pipeline/base_class.yaml
      eventType: 'base_event',
      onCapture: function () {
        var _onCapture = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee3(payload) {
          return regeneratorRuntime.wrap(function _callee3$(_context3) {
            while (1) {
              switch (_context3.prev = _context3.next) {
                case 0:
                  _context3.t0 = getBaseEventOnCaptureFields;
                  _context3.t1 = payload;
                  _context3.t2 = analytics;
                  _context3.t3 = appConfig;
                  _context3.next = 6;
                  return getCookies();

                case 6:
                  _context3.t4 = _context3.sent;
                  _context3.t5 = getDeviceId;
                  _context3.t6 = getNetworkConnectionType;
                  _context3.t7 = serverRequest;
                  _context3.t8 = {
                    analytics: _context3.t2,
                    appConfig: _context3.t3,
                    cookies: _context3.t4,
                    getDeviceId: _context3.t5,
                    getNetworkConnectionType: _context3.t6,
                    serverRequest: _context3.t7
                  };
                  return _context3.abrupt("return", (0, _context3.t0)(_context3.t1, _context3.t8));

                case 12:
                case "end":
                  return _context3.stop();
              }
            }
          }, _callee3);
        }));

        function onCapture(_x2) {
          return _onCapture.apply(this, arguments);
        }

        return onCapture;
      }(),
      // Decorate each event with any fields (e.g. event_occurred_msec_ago)
      // that must be set at transmit time (as opposed to invocation time)
      onTransport: function onTransport(payload) {
        return getBaseEventOnTransportFields(payload);
      }
    }, {
      // https://github.com/NerdWallet/app-schemata/blob/master/analytic_pipeline/user_navigated.yaml
      eventType: 'user_navigated',
      onCapture: function () {
        var _onCapture2 = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee4() {
          var cookies, payload;
          return regeneratorRuntime.wrap(function _callee4$(_context4) {
            while (1) {
              switch (_context4.prev = _context4.next) {
                case 0:
                  if (isInBrowser()) {
                    _context4.next = 2;
                    break;
                  }

                  return _context4.abrupt("return", {});

                case 2:
                  _context4.next = 4;
                  return getCookies();

                case 4:
                  cookies = _context4.sent;
                  payload = {
                    // decorate additional fields when in the browser
                    url: window.location.href,
                    initially_visible: !window.document.hidden,
                    web_referrer: window.document.referrer || undefined,
                    viewport_width: window.innerWidth,
                    viewport_height: window.innerHeight,
                    google_analytics_client_id: cookies[COOKIES.GOOGLE_ANALYTICS_CLIENT_ID_COOKIE_NAME]
                  };

                  if ('devicePixelRatio' in window) {
                    payload.pixel_ratio = window.devicePixelRatio;
                  }

                  return _context4.abrupt("return", payload);

                case 8:
                case "end":
                  return _context4.stop();
              }
            }
          }, _callee4);
        }));

        function onCapture() {
          return _onCapture2.apply(this, arguments);
        }

        return onCapture;
      }()
    }, {
      // https://github.com/NerdWallet/app-schemata/blob/master/analytic_pipeline/impression.yaml
      eventType: 'impression',
      onCapture: function onCapture(payload) {
        return {
          // impression_id needs to be overwritable by original payload
          // for <Impression /> components to work correctly
          impression_id: payload.impression_id || uuid(),
          navigation_id: analytics.getState('navigation_id')
        };
      }
    }, {
      // https://github.com/NerdWallet/app-schemata/blob/master/analytic_pipeline/element_interacted.yaml
      eventType: 'element_interacted',
      onCapture: function onCapture(payload) {
        return {
          // In the common case, interaction_id will not be provided explicitly
          // in the payload and the automatically generated value will be used,
          // but we still allow calls to `analytics.track` to specify it if
          // needed.
          interaction_id: payload.interaction_id || uuid(),
          navigation_id: analytics.getState('navigation_id')
        };
      }
    }, {
      // https://github.com/NerdWallet/app-schemata/blob/master/analytic_pipeline/feature_enrollment_changed.yaml
      eventType: 'feature_enrollment_changed',
      onCapture: function onCapture(payload) {
        return {
          // if the uuid is not sent in the payload, generate a uuid
          enrollment_change_id: payload.enrollment_change_id || uuid()
        };
      }
    }, {
      // this event is ONLY meant to be fired in BROWSER
      // https://github.com/NerdWallet/app-schemata/blob/master/analytic_pipeline/web_visibility_changed.yaml
      eventType: 'web_visibility_changed',
      onCapture: function onCapture() {
        return {
          navigation_id: analytics.getState('navigation_id'),
          action: window.document.visibilityState
        };
      }
    }]
  }); // Set initial `event_seqno`; this will be incremented for future events.
  // If the value was already set by another instance of analytics on the page,
  // DO NOT clobber it.

  if (!analytics.getState('event_seqno')) {
    analytics.setState('event_seqno', 1);
  } // wrap the `track` method to enforce that instances
  // configured for 'web' will throw error when called server-side


  function track(eventType) {
    var payload = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    if (platform === 'web' && !process.browser) {
      throw new Error(['Calling `track(eventType, payload)` from a browser analytics instance forbidden in non-browser environments.', 'Use the instance configured for server use instead.', 'See middleware located in `@nerdwallet/analytics/middleware.js`'].join('\n'));
    } // This logic used to run in a decorator for `user_navigated`. However, decorators
    // run asynchronously. This caused a race condition with React applications where
    // renders that occur immediately after a `user_navigated` event but before the
    // async decorator runs would read the old `navigation_id` value out of analytics
    // state instead of the new one.
    //
    // To fix this, we generate and store a new `navigation_id` synchronously
    // when the `user_navigated` event is fired.
    //
    // TODO: if we have a similar race condition in the future (or have a need to run
    // event side effects synchronously), we should consider refactoring our decorators
    // framework to include an `onTrack` function that would run synchronously when the
    // event is fired to complement the async `onCapture` decorator.
    //
    // This logic previously ran only for the `user_navigated` event. However,
    // this meant that any event tracked before a `user_navigated` event would
    // be missing a `navigation_id`, which was a data quality issue and
    // actually caused runtime errors due to Yak validation (see
    // https://nerdwallet.atlassian.net/browse/FEI-2591). To fix that, we
    // switched to running this logic in more situations. We'll now generate
    // new a new `navigation_id` in two scenarios:
    //
    // * If there is currently no `navigation_id` in our state. This handles the
    //   case of firing the first event on a page, `user_navigated` or otherwise.
    //
    // * If the current event is user_navigated AND we have already seen a
    //   `user_navigated` event on the page. This handles the case of firing
    //   subsequent `user_navigated` events where we definitely want to generate
    //   a new `navigation_id`.


    var needsNewNavigationId = analytics.getState('navigation_id') === undefined || analytics.getState('has_seen_user_navigated') === true && eventType === 'user_navigated';

    if (needsNewNavigationId) {
      var navigationId = uuid(); // The Data folks want to ensure that they can consistently reference
      // analytics events by the same pageview/navigation ID, regardless of
      // if they're coming from `@nerdwallet/analytics` or `nwa`. However, we
      // don't have guarantees about which library will handle navigation
      // events first. So, we'll allow them to race.
      // See: https://nerdwallet.atlassian.net/browse/FEI-2421

      if (typeof window !== 'undefined' && typeof window.nwa !== 'undefined' && window.nwa.getGlobalProp('pageViewId') != null) {
        // If we get here, we know that `nwa` has already been instantiated. That
        // leaves us with two scenarios:
        //
        // 1. `nwa` and `nwjs-analytics` are both booting up for the first time. We
        // can detect this scenario by checking if we have a `navigation_id` in our
        // state. If we do not, we'll just blindly accept `nwa`'s `pageViewId` as
        // our navigation ID.
        //
        // 2. `nwa` and `nwjs-analytics` already booted up a while ago, and we're now
        // observing a client-side navigation event; we'll know this is the case if we
        // already have a `navigation_id` in our state. If we do, we'll compare our
        // current `navigation_id` to `nwa`'s `pageViewId`. If they match, we know we
        // "got here first" and we'll use a new randomly-generated UUID. If they do not
        // match, we'll update our `navigation_id` to use `nwa`'s `pageViewId`.
        if (analytics.getState('navigation_id') == null) {
          // This is scenario 1: blindly use `nwa`'s `pageViewId` as our navigation ID.
          navigationId = window.nwa.getGlobalProp('pageViewId');
        } else {
          var currentPageViewId = window.nwa.getGlobalProp('pageViewId'); // This is scenario 2: compare our current `navigation_id` to `nwa`'s `pageViewId`.
          // If they don't match, we'll take the `pageViewId` from `nwa` as our
          // `navigation_id`. If they do match, we'll default to the random UUID
          // that we generated above.

          if (analytics.getState('navigation_id') !== currentPageViewId) {
            navigationId = currentPageViewId;
          }
        }
      }

      analytics.setState('previous_navigation_id', analytics.getState('navigation_id'));
      analytics.setState('navigation_id', navigationId);
    }

    if (eventType === 'user_navigated') {
      // Remember that we've encountered a `user_navigated` event in the past.
      // Crucially, this is done outside of the above conditional block.
      analytics.setState('has_seen_user_navigated', true); // We'll only blindly attach a `navigation_id` to the payload if this
      // is a `user_navigated` event. Otherwise, we'll let the event
      // decorators attach it as needed.
      // eslint-disable-next-line no-param-reassign

      payload.navigation_id = analytics.getState('navigation_id');
    }

    return analytics.track(eventType, payload);
  }

  return _objectSpread(_objectSpread({}, analytics), {}, {
    track: track,
    // Reexport this so that consumers don't have to pull in their own
    // UUID generating library
    uuid: uuid
  });
}